'use client';

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '@/config/api';
import Icon from '@/components/ui/AppIcon';

interface OrganizationDropdownProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  id?: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  className?: string;
}

export default function OrganizationDropdown({
  value,
  onChange,
  error,
  disabled = false,
  id = 'organization',
  placeholder = 'Enter your organization',
  label = 'Organization',
  required = false,
  className = '',
}: OrganizationDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [organizations, setOrganizations] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch organizations from API
  const fetchOrganizations = async (searchQuery: string) => {
    setIsLoading(true);
    try {
      const url = API_ENDPOINTS.organizations.names(searchQuery, 10);
      const response = await axios.get(url);
      if (Array.isArray(response.data)) {
        // Ensure max 10 records
        setOrganizations(response.data.slice(0, 10));
      } else {
        setOrganizations([]);
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setOrganizations([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch on focus or value change when open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        fetchOrganizations(value || '');
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [value, isOpen]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (orgName: string) => {
    onChange(orgName);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label htmlFor={id} className="block text-sm font-caption font-medium text-foreground mb-2">
          {label} {required && <span className="text-destructive">*</span>}
        </label>
      )}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
          <Icon name="BuildingOfficeIcon" size={20} variant="outline" className="text-muted-foreground" />
        </div>
        <input
          id={id}
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => {
            setIsOpen(true);
            fetchOrganizations(value || '');
          }}
          className={`w-full pl-10 pr-10 py-3 bg-background border rounded-md font-caption text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth ${error ? 'border-error' : 'border-border'
            }`}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center gap-1">
          {isLoading ? (
            <Icon name="ArrowPathIcon" size={18} variant="outline" className="text-muted-foreground animate-spin" />
          ) : (
            <button
              type="button"
              onClick={() => {
                if (!disabled) {
                  const nextState = !isOpen;
                  setIsOpen(nextState);
                  if (nextState) fetchOrganizations(value || '');
                }
              }}
              tabIndex={-1}
              className="text-muted-foreground hover:text-foreground focus:outline-none"
            >
              <Icon
                name="ChevronDownIcon"
                size={18}
                variant="outline"
                className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
          )}
        </div>
      </div>

      {/* Dropdown Options */}
      {isOpen && !disabled && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-elevation-3 max-h-60 overflow-y-auto py-1 animate-in fade-in slide-in-from-top-1 duration-200">
          {isLoading && organizations.length === 0 ? (
            <div className="px-4 py-3 text-xs text-muted-foreground flex items-center gap-2">
              <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
              <span>Loading organizations...</span>
            </div>
          ) : organizations.length > 0 ? (
            organizations.map((org, index) => (
              <button
                key={`${org}-${index}`}
                type="button"
                onClick={() => handleSelect(org)}
                className={`w-full text-left px-4 py-2.5 text-sm font-caption hover:bg-muted/80 hover:text-primary transition-colors flex items-center justify-between ${value === org ? 'bg-primary/10 font-semibold text-primary' : 'text-foreground'
                  }`}
              >
                <span className="truncate">{org}</span>
                {value === org && (
                  <Icon name="CheckIcon" size={16} variant="outline" className="text-primary flex-shrink-0" />
                )}
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-xs text-muted-foreground">
              No organizations found
            </div>
          )}
        </div>
      )}

      {error && <p className="mt-1 text-xs text-error font-caption">{error}</p>}
    </div>
  );
}
